import rimg from '@pixelunion/rimg-shopify';
import 'intersection-observer';
import EventHandler from '@pixelunion/events';

import ProductList from '../components/ProductList';

function setQueryParams() {
  window.Shopify.queryParams = {};

  if (window.location.search.length) {
    let aKeyValue;
    const aCouples = window.location.search.substr(1).split('&');

    for (let i = 0; i < aCouples.length; i++) {
      aKeyValue = aCouples[i].split('=');

      // Reset the page number when we apply (i.e. don't add it to params)
      if ((aKeyValue.length > 1) && (aKeyValue[0] !== 'page')) {
        window
          .Shopify
          .queryParams[decodeURIComponent(aKeyValue[0])] = decodeURIComponent(aKeyValue[1]);
      }
    }
  }
}

function updateSortFilter(e) {
  let search;
  const Sorting = {};
  Sorting.sort_by = document.querySelector('[data-sorting] select').value;

  if (e.target.closest('[data-sorting]').classList.contains('vendor-collection')) {
    const currentSearch = window.location.search;
    const searchParts = currentSearch.split('&');

    for (let index = 0; index < searchParts.length; index++) {
      const part = searchParts[index];
      if (part.indexOf('sort_by') !== -1) {
        searchParts.splice(index, 1);
      }
    }

    search = searchParts.join('&');
    window.location.search = `${search}&${Object.keys(Sorting).map(k => `${encodeURIComponent(k)}=${encodeURIComponent(Sorting[k])}`).join('&')}`;
  } else {
    window.location.search = Object.keys(Sorting).map(k => `${encodeURIComponent(k)}=${encodeURIComponent(Sorting[k])}`).join('&');
  }
}

function updateTagFilter(e) {
  const { collection } = e.target.dataset;
  const tag = e.target.value;

  const shopifyParamsString = Object.keys(window.Shopify.queryParams).map(k => `${encodeURIComponent(k)}=${encodeURIComponent(window.Shopify.queryParams[k])}`).join('&');

  if (tag === 'all') {
    window.location.href = `${window.Theme.routes.collections_url}/${collection}?${shopifyParamsString}`;
  } else {
    window.location.href = `${window.Theme.routes.collections_url}/${collection}/${tag}?${shopifyParamsString}`;
  }
}

export default class Collection {
  constructor(section) {
    this.events = new EventHandler();
    this.el = section.el;
    this.data = section.data;
    this._infiniteScrollHandler = this._infiniteScrollHandler.bind(this);
    this.productList = new ProductList({ el: this.el, id: `shopify-section-${section.id}` });

    this.collectionTagFilterSelect = document.querySelector('[data-tag-filtering] select');
    this.collectionSortFilterSelect = document.querySelector('[data-sorting] select');

    if (section.type === 'static-collection') {
      setQueryParams();
      this._initializeInfiniteScroll();
      this._bindEvents();
    }
  }

  _bindEvents() {
    if (this.collectionTagFilterSelect) {
      this.events.register(this.el.querySelector('[data-tag-filtering] select'), 'change', updateTagFilter);
    }

    if (this.collectionSortFilterSelect) {
      this.events.register(this.el.querySelector('[data-sorting] select'), 'change', updateSortFilter);
    }

    if (this.enableInfiniteScroll) {
      this.events.register(this.showMoreButton, 'click', this._infiniteScrollHandler);
    }
  }

  _initializeInfiniteScroll() {
    // Infinite scroll
    if (this.data.pagination_method.indexOf('infinite_scroll') > -1
      && this.data.products_per_page < this.data.total_products
      && window.location.search.indexOf('page') === -1
    ) {
      this.loadMoreTarget = this.el.querySelector('[data-infinite-scroll-target]');
      this.showMoreButton = this.el.querySelector('[data-infinite-scroll-show-more]');
      this.showMoreButton.style.display = 'inline-block';
      this.productsLoadedCount = this.el.querySelectorAll('[data-product-item-id]').length;

      // If we're infinite scrolling, we want to hide pagination
      // while still keeping it accessible for screenreaders.
      const pagination = this.el.querySelector('.pagination');
      pagination.style.position = 'absolute';
      pagination.style.left = '-9999px';

      this.currentPage = 1;
      this.fullCollectionLoaded = false;
      this.enableInfiniteScroll = true;

      if (this.data.pagination_method === 'infinite_scroll') {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.intersectionRatio > 0) {
              this.scrollTargetVisible = true;
              this._infiniteScroll();
            } else {
              this.scrollTargetVisible = false;
            }
          });
        });

        observer.observe(this.loadMoreTarget);
      }
    }
  }

  _infiniteScrollHandler() {
    // Use handler so _infiniteScroll isn't called with event as argument
    this._infiniteScroll();
  }

  _infiniteScroll(autoScrollCount = 0) {
    if (this.fullCollectionLoaded || autoScrollCount > 2) {
      return;
    }
    const currentPage = `page=${this.currentPage}`;
    this.currentPage++;

    const nextPage = `page=${this.currentPage}`;
    let url = `${window.location.pathname}${window.location.search}`;

    if (window.location.search.indexOf('page') > -1) {
      url = url.replace(currentPage, nextPage);
    } else if (window.location.search.indexOf('?') > -1) {
      url = `${url}&${nextPage}`;
    } else {
      url = `${url}?${nextPage}`;
    }

    this.showMoreButton.classList.add('loading');
    this.showMoreButton.innerHTML = this.showMoreButton.dataset.infiniteScrollLoading;

    const xhr = new XMLHttpRequest();

    xhr.onload = () => {
      const { response } = xhr;
      const products = response.querySelectorAll('[data-product-item-id]');
      const collectionGrid = document.querySelector('[data-collection-grid]');

      products.forEach(product => {
        collectionGrid.appendChild(product);
        this.productsLoadedCount++;
        this.productList.reload();

        // Add a timeout to give rimg a little more time to load placeholder SVG
        // to prevent images from loading at the wrong size in Firefox
        setTimeout(() => rimg.watch(product), 2);
      });

      if (this.productsLoadedCount === this.data.total_products) {
        // we've loaded the full collection
        this.fullCollectionLoaded = true;
        this.showMoreButton.style.display = 'none';
      }

      this.showMoreButton.classList.remove('loading');
      this.showMoreButton.innerHTML = this.showMoreButton.dataset.infiniteScrollShowMore;

      // If target is still in viewport, we want to load more items.
      // But let's keep track of how many times we've done this, and
      // take a bit of time to let IntersectionObserver catch up.
      setTimeout(() => {
        if (this.scrollTargetVisible) {
          this._infiniteScroll(autoScrollCount + 1);
        }
      }, 200);
    };
    xhr.open('GET', url, true);
    xhr.responseType = 'document';
    xhr.send();
  }

  onSectionUnload() {
    this.events.unregisterAll();

    if (this.productList) {
      this.productList.unload();
    }
  }
}
